import React, { useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useSelector } from 'react-redux';

import gfpOfferStore, {
  IGfpOfferFormFilter,
} from 'store/state/gfpOffer/gfpOfferStore';
import { FormInstance, useForm } from 'antd/lib/form/Form';
import { Button, Col, Form, Input, Row, Select } from 'antd';
import { IQualityObservation } from 'model/QualityObservation';
import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import { Moment } from 'moment';
import { getLastFastBuySuccess } from 'store/state/cart/cartSelectors';
import * as S from './GfpOfferFilter.styled';

const underText =
  'Busque por palavras que possam estar contidas na observação adicionada pelo produtor';

const brFormat = (value: Moment) => value.format('DD/MM/YYYY');

type IProps = {
  lkpOfferId: number;
  offerType: number;
  packingId?: string;
  auctionDate?: string;
};

export default function GfpOfferFilter(props: IProps) {
  const {
    filters,
    loadFilters,
    setFormInstance,
    resetStatesToDefault,
    handleSearch,
  } = gfpOfferStore(
    useShallow((state) => ({
      filters: state.filters,
      loadFilters: state.loadFilters,
      setFormInstance: state.setFormInstance,
      resetStatesToDefault: state.resetStatesToDefault,
      handleSearch: state.handleSearch,
    })),
  );

  const lastFastBuySuccess = useSelector(getLastFastBuySuccess);

  useEffect(
    () => () => {
      resetStatesToDefault();
    },
    [resetStatesToDefault],
  );

  const { lkpOfferId, offerType, packingId, auctionDate } = props;

  const [formFilter] = useForm<IGfpOfferFormFilter>();

  useEffect(() => {
    const instance = {
      validateFields: formFilter.validateFields,
      getFieldValue: formFilter.getFieldValue,
      getFieldsValue: formFilter.getFieldsValue,
    } as Partial<FormInstance<IGfpOfferFormFilter>>;

    setFormInstance(instance);
  }, [formFilter, setFormInstance]);

  useEffect(() => {
    loadFilters();
    handleSearch(lkpOfferId, offerType, packingId, auctionDate);
  }, [
    loadFilters,
    handleSearch,
    lkpOfferId,
    offerType,
    packingId,
    auctionDate,
  ]);

  useEffect(() => {
    if (lastFastBuySuccess) {
      handleSearch(lkpOfferId, offerType, packingId, auctionDate);
    }
  }, [
    lastFastBuySuccess,
    handleSearch,
    lkpOfferId,
    offerType,
    packingId,
    auctionDate,
  ]);

  const onClear = useCallback(() => {
    formFilter.resetFields();
  }, [formFilter]);

  const onSearch = useCallback(async () => {
    await formFilter.validateFields();

    handleSearch(lkpOfferId, offerType, packingId, auctionDate);
  }, [handleSearch, lkpOfferId, offerType, formFilter, packingId, auctionDate]);

  return (
    <S.Container>
      <Form layout="vertical" form={formFilter}>
        <Row gutter={8}>
          <Col lg={7} sm={12} xs={24}>
            <Form.Item label="Data de Entrega CVH" name="deliveryDate">
              <S.DatePickerFilter
                placeholder="Selecione uma data"
                allowClear
                format={brFormat}
              />
            </Form.Item>
          </Col>
          <Col lg={7} sm={12} xs={24}>
            <Form.Item label="Observação da GFP" name="observationIds">
              <Select
                placeholder="Selecione uma ou mais opções"
                showSearch
                allowClear
                showArrow
                loading={filters.isObservationLoading}
                disabled={filters.isObservationLoading}
                optionLabelProp="label"
                optionFilterProp="children"
              >
                {filters.observations.map((item: IQualityObservation) => (
                  <Select.Option
                    key={item.code}
                    value={item.id}
                    label={item.description}
                    title={item.description}
                  >
                    {item.description}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={8} sm={20} xs={24}>
            <S.FormContainer>
              <Form.Item label="Palavra-chave" name="keyword">
                <Input placeholder="Digite aqui para buscar" />
              </Form.Item>
              <S.FormUndertextContainer>
                <S.FormUndertext>{underText}</S.FormUndertext>
              </S.FormUndertextContainer>
            </S.FormContainer>
          </Col>
          <Col
            style={{ paddingTop: 30 }}
            lg={2}
            sm={4}
            xs={24}
            className="buttonCol"
          >
            <Row gutter={8} justify="end" wrap={false}>
              <Col>
                <Button
                  onClick={onClear}
                  type="default"
                  aria-label="clean-filters"
                >
                  <S.ButtonText>Limpar filtros</S.ButtonText>
                  <ClearOutlined />
                </Button>
              </Col>
              <Col>
                <Button
                  onClick={onSearch}
                  type="primary"
                  htmlType="submit"
                  aria-label="search"
                >
                  <S.ButtonText>Pesquisar</S.ButtonText>
                  <SearchOutlined />
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </S.Container>
  );
}
